import { childGradeOptions } from '@lib/enums/registration-form'
import { CClient, showErrorAlert } from '@common/utils'
import Button from '@components/atoms/button'
import Input from '@components/common/forms/input'
import SelectInput from '@components/common/forms/select'
import Modal from '@components/common/modal'
import { validateProfileData } from '@containers/book-trial-class/validators'
import { useProfileContext } from '@contexts/profile'
import { GradeType } from '@lib/types/common'
import { getAPIErrorString } from '@lib/utils/common'
import { captureException } from '@sentry/node'
import cogoToast from 'cogo-toast'
import React, { Dispatch, FormEvent, SetStateAction, useState } from 'react'

type Props = {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
}

const UpdateDetailsModal = ({ isOpen, setIsOpen }: Props) => {
  const { profileDetails, setProfileDetails } = useProfileContext()

  const [name, setName] = useState(profileDetails?.name || '')
  const [grade, setGrade] = useState({
    value: profileDetails?.student?.grade || 'select',
    label: profileDetails?.student?.grade || 'Select grade/class',
  })
  const [bio, setBio] = useState(profileDetails?.bio || '')
  const [hobbies, setHobbies] = useState(profileDetails?.hobbies || '')
  const [errors, setErrors] = useState<any>({})
  const [isLoading, setIsLoading] = useState(false)

  const handleSelectChange = (
    id: string,
    value: {
      value: string
      label: string
    }
  ) => setGrade(value)

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = {
      name,
      student: {
        id: profileDetails?.student?.id,
        grade: grade.value,
      },
      bio,
      hobbies,
    }

    if (name === profileDetails?.name) delete formData.name
    if (bio === profileDetails?.bio) delete formData.bio
    if (hobbies === profileDetails?.hobbies) delete formData.hobbies
    if (grade.value === profileDetails?.student?.grade) delete formData.student

    if (isLoading) return
    setIsLoading(true)

    try {
      const errors = validateProfileData(formData)
      if (Object.keys(errors).length) {
        setErrors(errors)
        setIsLoading(false)
        return
      }

      const url = '/api/v1/users/me/'
      const body = JSON.stringify(formData)

      const response = await CClient(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      })

      if (response.ok) {
        const updatedProfileDetails = profileDetails
        updatedProfileDetails.name = name
        updatedProfileDetails.bio = bio
        updatedProfileDetails.hobbies = hobbies
        updatedProfileDetails.student.grade = grade.value as GradeType
        setProfileDetails({ ...updatedProfileDetails })
        cogoToast.success('Your profile has been updated successfully.')
        setErrors({})
        setIsOpen(false)
      } else if (response.status === 400) {
        const error = await response.json()
        setIsLoading(false)
        setErrors(error)
      } else throw new Error(await getAPIErrorString(response, url, body))
    } catch (error) {
      console.error(error)
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while updating your profile. Please refresh the page and try again.',
      })
    }

    setIsLoading(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isCrossIconShown
      width="w-full md:max-w-lg"
    >
      <h1 className="text-2xl font-600 text-grey mb-3">Update your details</h1>
      <form className="flex flex-col" onSubmit={handleFormSubmit}>
        <Input
          label="Name"
          id="name"
          name="name"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          errorText={errors['name']}
        />
        <div className="mb-2">
          <SelectInput
            label="Student grade"
            id="childGrade"
            placeholder="Select"
            value={grade}
            handleSelectChange={handleSelectChange}
            options={childGradeOptions}
            errorText={errors['childGrade']}
            isInModal
            required
          />
        </div>

        <div className={`text-grey font-600`}>Short bio</div>
        <textarea
          className="my-2 form-input w-full h-32"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder="Short bio"
        />
        {errors?.bio && (
          <div className="my-2 leading-4 text-red-200 font-400">
            {errors?.bio}
          </div>
        )}
        <Input
          label="Hobby"
          id="hobbies"
          name="hobbies"
          placeholder="Hobby"
          value={hobbies}
          onChange={(e) => setHobbies(e.currentTarget.value)}
          errorText={errors['hobbies']}
        />
        {errors?.nonFieldErrors && (
          <div className="my-2 leading-4 text-red-200 font-400">
            {errors?.nonFieldErrors}
          </div>
        )}
        <div className="flex space-x-5 mt-2">
          <Button>Save</Button>
          <button
            className="text-orange hover:underline focus:outline-none font-600"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default React.memo(UpdateDetailsModal)
